import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import useScreenSize from '../../../hooks/useScreenSize';
import {selectCompanies} from '../../../store/companies';
import {selectProjectsSorted} from '../../../store/selectors';
import {formatCurrency} from '../../../utils';
import ProjectLabel from '../../../components/common/ProjectLabel';

const pad2 = n => String(n).padStart(2, '0');
const localDateStr = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const workingDaysInMonth = (year, month) => {
    const d = new Date(year, month, 1);
    let count = 0;
    while (d.getMonth() === month) {
        const day = d.getDay();
        if (day !== 0 && day !== 6) count++;
        d.setDate(d.getDate() + 1);
    }
    return count;
};

const toUah = (amount, currency, currencyRates, todayStr) => {
    if (currency === 'UAH') return amount;
    const sorted = [...currencyRates].sort((a, b) => a.date.localeCompare(b.date));
    const applicable = sorted.reduce((found, r) => (r.date <= todayStr ? r : found), null);
    if (!applicable) return amount;
    if (currency === 'USD') return amount * (Number(applicable.uahToUsdRate) || 1);
    if (currency === 'EUR') return amount * (Number(applicable.uahToEurRate) || 1);
    return amount;
};

const deriveRate = (movement, workingDays) => {
    const md = movement.movementDetails;
    if (md?.type === 'RATE_BASED') return Number(md.rate);
    return movement.gross / workingDays / 8;
};

const ProjectIncomeTable = ({movements, currencyRates, selectedMonth}) => {
    const {isCompact} = useScreenSize();
    const projects = useSelector(selectProjectsSorted);
    const companies = useSelector(selectCompanies);
    const [y, m] = selectedMonth.split('-').map(Number);
    const monthIndex = m - 1;
    const todayStr = localDateStr(new Date(y, monthIndex + 1, 0));
    const workingDays = workingDaysInMonth(y, monthIndex);

    const rows = useMemo(() => {
        const incomes = movements.filter(
            m => m.type === 'INCOME' && m.movementDetails?.type !== 'REVALUATION',
        );

        const groups = {};
        for (const m of incomes) {
            const key = `${m.projectId ?? 'direct'}__${m.currencyCode}`;
            if (!groups[key]) {
                groups[key] = {projectId: m.projectId ?? null, projectTitle: m.projectTitle ?? 'Other Income', currencyCode: m.currencyCode, income: 0, movements: []};
            }
            groups[key].income += m.gross;
            groups[key].movements.push(m);
        }

        return Object.values(groups)
            .map(g => ({
                ...g,
                hourRate: g.movements.reduce((s, m) => s + deriveRate(m, workingDays), 0) / g.movements.length,
            }))
            .sort((a, b) => b.income - a.income);
    }, [movements, workingDays]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!rows.length)
        return (<p className="dashboard__empty">No incomes yet.</p>);

    const currencies = new Set(rows.map(r => r.currencyCode));
    const multipleCurrencies = currencies.size > 1;
    const displayCurrency = multipleCurrencies ? 'UAH' : [...currencies][0];

    const totalIncome = multipleCurrencies
        ? rows.reduce((s, r) => s + toUah(r.income, r.currencyCode, currencyRates, todayStr), 0)
        : rows.reduce((s, r) => s + r.income, 0);

    const totalRate = totalIncome / workingDays / 8;

    return (
        <div className="last-month__table-card">
            <table className="last-month__table project-income-table">
                <thead>
                <tr>
                    <th>Project</th>
                    <th>Income</th>
                    <th>Hour rate</th>
                </tr>
                </thead>
                <tbody>
                {rows.map(row => (
                    <tr key={`${row.projectTitle}__${row.currencyCode}`}>
                        <td className="last-month__main-cell">
                            <ProjectLabel
                                project={{title: row.projectTitle}}
                                company={companies.find(c => c.id === projects.find(p => p.id === row.projectId)?.companyId) ?? null}
                            />
                        </td>
                        <td className="last-month__value positive-value">
                            {formatCurrency(row.income, row.currencyCode, isCompact)}
                        </td>
                        <td className="last-month__value">
                            {formatCurrency(row.hourRate, row.currencyCode, isCompact)}/h
                        </td>
                    </tr>
                ))}
                </tbody>
                <tfoot>
                <tr className="project-income-table__total">
                    <td className="last-month__main-cell">
                        <span className="last-month__account-title">Total</span>
                    </td>
                    <td className="last-month__value positive-value">
                        {formatCurrency(totalIncome, displayCurrency, isCompact)}
                    </td>
                    <td className="last-month__value">
                        {formatCurrency(totalRate, displayCurrency, isCompact)}/h
                    </td>
                </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default ProjectIncomeTable;
